import { useEffect, useState } from "react";
import { ArrowLeftOutlined, CloseOutlined } from "@ant-design/icons";
import { message, Row, Form } from "antd";
import { useToken } from "../../../common/helper";
import alertIcon from "../../../assets/images/alertGroup.svg";
import truckIcon from "../../../assets/images/truck.svg";
import locationIcon from "../../../assets/images/location.svg";
import clockIcon from "../../../assets/images/clock.svg";
import { useSpin } from "../../../components/SpinContext";
import CustomButton from "../../../components/CustomButton";
import CustomParagraph from "../../../components/CustomParagraph";
import CustomTitle from "../../../components/CustomTitle";
import ShipmentAddNotificationForm from "./ShipmentAddNotificationForm";
import {
  deleteShipmentNotification,
  shipmentNotificationListing,
} from "../../../api's/shipment";
import NotificationListSection from "./NotificationListSection";
import CustomModalWithoutButtons from "../../../components/CustomModalWithoutButtons";

const ShipmentNotificationDrawer = ({ toggleDrawer, shipmentId }) => {
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNotificationMethod, setSelectedNotificationMethod] =
    useState(null);
  const [selectedUserGroup, setSelectedUserGroup] = useState([]);
  const [selectedThresholds, setSelectedThresholds] = useState([]);
  const [aiMessage, setAiMessage] = useState("");
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState(1);
  const [isEditing, setIsEditing] = useState(true);
  const productList = [];
  const notificationPermissions = { create_self: true };

  const [notificationsData, setNotificationsData] = useState({});
  const notificationTypes = [
    {
      id: "CRITICAL_ALERT",
      label: "Critical & At-Risk Alert",
      icon: alertIcon,
      description:
        "An alert noting an issue that has potentially or could lead to a compromised product",
    },
    {
      id: "PICKUP_DELIVERY",
      label: "Pickup / Delivery Confirmation",
      icon: truckIcon,
      description:
        "Notification when there has been a successful pick-up or delivery",
    },
    {
      id: "AT_LOCATION",
      label: "At Location",
      icon: locationIcon,
      description:
        "Notification when a product is at a specific location (e.g., a warehouse)",
    },
    {
      id: "ETA_BASED",
      label: "ETA",
      icon: clockIcon,
      description: "Notification when projected ETA moves by a set time",
    },
  ];
  const unauthorize = () => {};

  const handleCreate = () => {
    form.resetFields();
    setIsEditing(false);
    setAiMessage("");
    setSelectedType(null);
    setModalOpen(true);
    setStep(1);
    setSelectedThresholds([]);
    setSelectedNotificationMethod(null);
    setHoveredCardId(null);
    setSelectedUserGroup([]);
  };

  const mapAlertsToTableData = (alerts) => {
    const getFrequencyLabel = (frequency, alert) => {
      if (!frequency) return "—";

      switch (frequency) {
        case "ONCE_A_DAY":
          return `Once a day at ${alert.time_1 || "—"}`;
        case "TWICE_A_DAY":
          return `Twice a day at ${alert.time_1 || "—"} & ${
            alert.time_2 || "—"
          }`;
        case "WEEKLY":
          return `Weekly on ${alert.day || "—"} at ${alert.time_2 || "—"}`;
        case "REALTIME":
          return "Real Time";
        case "BATCHEDALERTS":
          return "Batched Alerts";
        case "15MIN":
          return "Every 15 Mins";
        case "30MIN":
          return "Every 30 Mins";
        case "1HR":
          return "Every 1 Hour";
        default:
          return "—";
      }
    };

    const formatDate = (iso) => {
      if (!iso) return "—";
      const options = {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      };
      return new Date(iso).toLocaleString("en-US", options);
    };

    return alerts.map((alert) => {
      let alertTypeLabel = "";

      const isCritical = alert.is_critical;
      const isAtRisk = alert.is_at_risk;
      const isPickup = alert.is_pickup_confirmation;
      const isDelivery = alert.is_delivery_confirmation;

      if (isCritical && isAtRisk) {
        alertTypeLabel = "Critical & At-risk Alert";
      } else if (isCritical) {
        alertTypeLabel = "Critical Alert";
      } else if (isAtRisk) {
        alertTypeLabel = "At-risk Alert";
      }

      if (isPickup && isDelivery) {
        alertTypeLabel = "Pickup / Delivery Confirmation";
      } else if (isPickup) {
        alertTypeLabel = "Pickup Confirmation";
      } else if (isDelivery) {
        alertTypeLabel = "Delivery Confirmation";
      } else if (
        alert.alert_type === "AT_LOCATION" ||
        alert.alert_type === "at_location"
      ) {
        alertTypeLabel = "At Location";
      } else if (
        alert.alert_type === "ETA_BASED" ||
        alert.alert_type === "eta_based"
      ) {
        alertTypeLabel = "ETA";
      }

      return {
        key: alert.id,
        type: alertTypeLabel,
        products: alert.products.map((p) => p.name),
        frequency: getFrequencyLabel(alert.frequency, alert),
        send_to: alert?.send_to || [],
        createdBy: alert.created_by_name || "—",
        lastUpdated: formatDate(alert.updated_at),
        originalData: alert,
      };
    });
  };
  const fetchNotifications = () => {
    shipmentNotificationListing(token, shipmentId)
      .then((res) => {
        const data = res?.data?.data?.data;
        if (data?.shipment_specific || data?.company_level) {
          const companyLevelData = mapAlertsToTableData(
            data.company_level || []
          );
          setNotificationsData({
            companyLevel: companyLevelData || [],
            shipmentSpecific: data.shipment_specific || [],
          });
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  useEffect(() => {
    showSpin();
    fetchNotifications();
  }, [shipmentId, token]);

  console.log({ notificationsData });
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "0 0 16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <ArrowLeftOutlined
            onClick={() => toggleDrawer("open", "Detail", shipmentId)}
            style={{ fontSize: "14px", cursor: "pointer" }}
          />
          <span
            title="Close"
            className="no-bg"
            style={{ padding: "4px", cursor: "pointer" }}
            onClick={() => toggleDrawer("close")}
          >
            <CloseOutlined style={{ fontSize: "14px", color: "#000" }} />
          </span>
        </div>
        <CustomTitle
          text="Shipment notifications"
          fontSize="20px"
          lineHeight="28px"
          fontWeight="700"
          marginBottom="4px"
        />
        <CustomParagraph
          text="View all notifications that apply to this shipment and make custom, shipment-specific ones"
          fontSize="14px"
          color="#5E5E5E"
          marginBottom="12px"
        />
      </div>

      <div
        style={{
          overflowY: "auto",
          paddingRight: 8,
          paddingBottom: 100,
          flex: 1,
        }}
      >
        <NotificationListSection
          title="Company–level"
          list={notificationsData.companyLevel}
          isCompanyLevel
          onCreate={handleCreate}
        />

        <NotificationListSection
          title="Shipment-specific"
          list={notificationsData.shipmentSpecific}
          isCompanyLevel={false}
          onCreate={handleCreate}
          onEdit={(item) => {
            form.resetFields();
            const selectedCopy = { ...item };
            setSelected(null);
            setSelected(selectedCopy);
            setIsEditing(true);

            const foundType = notificationTypes.find(
              (t) => t.id === item?.alert_type
            );
            setSelectedType(foundType);
            setStep(2);
            setModalOpen(true);
          }}
          onDelete={(item) => {
            deleteShipmentNotification(item?.id, token).then((res) => {
              const data = res?.data;
              if (data?.data?.is_success) {
                message.success(data?.data?.message);
                fetchNotifications();
              }
            });
          }}
        />
      </div>

      <div
        style={{
          background: "#fff",
          position: "absolute",
          bottom: "0",
          left: "0",
          width: "100%",
          padding: "0 24px 24px",
        }}
      >
        <Row gutter={[0, 8]}>
          <CustomButton
            text="Add notification"
            bgColor="#000"
            color="#fff"
            fontSize="16px"
            fontWeight="600"
            height="48px"
            width="100%"
            borderRadius="8px"
            boxShadow="none"
            onClick={handleCreate}
          />
        </Row>
      </div>

      <CustomModalWithoutButtons
        modalOpen={modalOpen}
        modalSize={600}
        setModalOpen={setModalOpen}
      >
        <ShipmentAddNotificationForm
          form={form}
          aiMessage={aiMessage}
          setAiMessage={setAiMessage}
          selected={selected}
          notificationTypes={notificationTypes}
          shipmentId={shipmentId}
          fetchNotifications={fetchNotifications}
          setHoveredCardId={setHoveredCardId}
          hoveredCardId={hoveredCardId}
          selectedThresholds={selectedThresholds}
          setSelectedThresholds={setSelectedThresholds}
          selectedUserGroup={selectedUserGroup}
          setSelectedUserGroup={setSelectedUserGroup}
          onClose={() => {
            setModalOpen(false);
          }}
          notificationPermissions={notificationPermissions}
          isEditing={isEditing}
          productList={productList}
          selectedNotificationMethod={selectedNotificationMethod}
          setSelectedNotificationMethod={setSelectedNotificationMethod}
          unauthorize={unauthorize}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          step={step}
          setStep={setStep}
        />
      </CustomModalWithoutButtons>
    </div>
  );
};

export default ShipmentNotificationDrawer;
